import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../utils/api';
import { toast } from 'sonner';
import BadgeAvatar from '../components/BadgeAvatar';

const CreateMockExam = ({ user, logout }) => {
  const navigate = useNavigate();
  const STORAGE_KEY = 'mockExamProgress';
  
  // Initialize case studies from localStorage or empty
  const [caseStudies, setCaseStudies] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        toast.success('Loaded saved progress!');
        return parsed.caseStudies;
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
    return Array(15).fill(null).map(() => ({
      diagnosis: '',
      signs_symptoms: '',
      tongue: '',
      pulse: '',
      points: ''
    }));
  });
  
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedExam, setGeneratedExam] = useState(null);
  const [model, setModel] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.model || 'gpt-4o-mini';
      }
    } catch (error) {
      return 'gpt-4o-mini';
    }
    return 'gpt-4o-mini';
  });
  const [timeLimit, setTimeLimit] = useState(8100);
  const [lastSaved, setLastSaved] = useState(null);

  const handleCaseStudyChange = (index, field, value) => {
    const newCaseStudies = [...caseStudies];
    newCaseStudies[index][field] = value;
    setCaseStudies(newCaseStudies);
  };

  const toggleExpanded = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  const saveProgress = () => {
    try {
      const dataToSave = {
        caseStudies,
        model,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      setLastSaved(new Date());
      toast.success('Progress saved successfully!');
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    }
  };

  const clearSavedProgress = () => {
    if (window.confirm('Are you sure you want to clear all saved data? This cannot be undone.')) {
      localStorage.removeItem(STORAGE_KEY);
      setCaseStudies(Array(15).fill(null).map(() => ({
        diagnosis: '',
        signs_symptoms: '',
        tongue: '',
        pulse: '',
        points: ''
      })));
      setModel('gpt-4o-mini');
      setExpandedIndex(0);
      setLastSaved(null);
      toast.success('Saved progress cleared');
    }
  };

  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 720; // 12 minutes max (720 * 1 second) - generation takes ~7-8 min
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await api.get(`/mock-exams/generate-status/${jobId}`);
        const job = response.data;

        setGenerationProgress(job.progress || 0);
        setGenerationStep(job.current_step || '');

        if (job.status === 'completed') {
          setGeneratedExam(job.result);
          toast.success(`Mock exam generated successfully with ${job.result.total_questions} questions!`);
          setIsGenerating(false);
          return;
        } else if (job.status === 'failed') {
          toast.error(`Generation failed: ${job.error || 'Unknown error'}`);
          setIsGenerating(false);
          return;
        }

        // Poll every 1 second
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      } catch (error) {
        console.error('Error polling job status:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    // Timeout
    toast.error('Still generating after 12 minutes. The exam may still finish in the background — please refresh and check, or try again.');
    setIsGenerating(false);
  };

  const handleGenerate = async () => {
    // Validate all case studies are fully filled
    for (let i = 0; i < caseStudies.length; i++) {
      const cs = caseStudies[i];
      const missingFields = [];
      if (!cs.diagnosis.trim()) missingFields.push('Diagnosis');
      if (!cs.signs_symptoms.trim()) missingFields.push('Signs & Symptoms');
      if (!cs.tongue.trim()) missingFields.push('Tongue');
      if (!cs.pulse.trim()) missingFields.push('Pulse');
      if (!cs.points.trim()) missingFields.push('Points');
      
      if (missingFields.length > 0) {
        toast.error(`Case Study #${i + 1} is missing: ${missingFields.join(', ')}`);
        setExpandedIndex(i); // Expand the incomplete case study
        return;
      }
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStep('Starting generation...');

    try {
      // Start async generation
      const response = await api.post('/mock-exams/generate-async', {
        case_studies: caseStudies,
        model
      });
      
      const jobId = response.data.job_id;
      toast.success('Generation started! This will take about 7-8 minutes. Please keep this page open.');
      
      // Start polling for status
      await pollJobStatus(jobId);
    } catch (error) {
      console.error('Error starting mock exam generation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start generation');
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedExam) return;

    try {
      await api.post('/mock-exams/save', {
        case_studies: generatedExam.case_studies,
        case_scenarios: generatedExam.case_scenarios,
        questions: generatedExam.questions,
        time_limit: timeLimit,
        is_featured: true
      });
      
      toast.success('Mock exam saved and featured successfully!');
      navigate('/admin');
    } catch (error) {
      console.error('Error saving mock exam:', error);
      toast.error(error.response?.data?.detail || 'Failed to save mock exam');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className="flex items-center space-x-4">
              {user && (
                <div className="flex items-center space-x-3">
                  <BadgeAvatar user={user} size="md" />
                  <div className="text-right hidden md:block">
                    <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                  <Button variant="outline" onClick={logout}>Logout</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Title */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Create Case Study Mock Exam
            </h1>
            <p className="text-gray-600">
              Enter 15 detailed case studies to generate a comprehensive 90-question mock exam (6 questions per case study)
            </p>
          </div>

          {!generatedExam ? (
            <>
              {/* Save Progress Section */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-blue-900">Save Your Progress</p>
                      {lastSaved && (
                        <p className="text-xs text-blue-700">
                          Last saved: {lastSaved.toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={saveProgress}
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Progress
                    </Button>
                    <Button
                      onClick={clearSavedProgress}
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Clear Saved
                    </Button>
                  </div>
                </div>
              </div>

              {/* Model Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  AI Model
                </label>
                <select
                  value={model}
                  onChange={(e) => {
                    setModel(e.target.value);
                    // Auto-save when model changes
                    setTimeout(() => {
                      try {
                        const dataToSave = {
                          caseStudies,
                          model: e.target.value,
                          timestamp: new Date().toISOString()
                        };
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
                      } catch (error) {
                        console.error('Auto-save error:', error);
                      }
                    }, 100);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <optgroup label="OpenAI">
                    <option value="gpt-5">GPT-5 (Most Advanced)</option>
                    <option value="gpt-5-mini">GPT-5 Mini</option>
                    <option value="gpt-4.1">GPT-4.1</option>
                    <option value="gpt-4o">GPT-4o (Balanced)</option>
                    <option value="gpt-4o-mini">GPT-4o Mini (Fast)</option>
                  </optgroup>
                  <optgroup label="Anthropic Claude">
                    <option value="claude-4-opus">Claude 4 Opus (Most Capable)</option>
                    <option value="claude-4-sonnet">Claude 4 Sonnet</option>
                    <option value="claude-3-7-sonnet">Claude 3.7 Sonnet</option>
                    <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                  </optgroup>
                  <optgroup label="Google Gemini">
                    <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                  </optgroup>
                </select>
              </div>

              {/* Case Studies Input - Expandable Forms */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  Case Studies (15 required)
                </label>
                
                <div className="space-y-3">
                  {caseStudies.map((caseStudy, index) => {
                    const isExpanded = expandedIndex === index;
                    const isComplete = caseStudy.diagnosis && caseStudy.signs_symptoms && 
                                       caseStudy.tongue && caseStudy.pulse && caseStudy.points;
                    
                    return (
                      <div key={index} className="border border-gray-300 rounded-lg overflow-hidden">
                        {/* Header - Always visible */}
                        <button
                          onClick={() => toggleExpanded(index)}
                          className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="font-semibold text-gray-700">Case Study #{index + 1}</span>
                            {caseStudy.diagnosis && (
                              <span className="text-sm text-gray-600">- {caseStudy.diagnosis}</span>
                            )}
                            {isComplete && (
                              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <svg 
                            className={`w-5 h-5 text-gray-600 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {/* Expandable Form Fields */}
                        {isExpanded && (
                          <div className="p-4 space-y-4 bg-white">
                            {/* Diagnosis */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Diagnosis Name <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={caseStudy.diagnosis}
                                onChange={(e) => handleCaseStudyChange(index, 'diagnosis', e.target.value)}
                                placeholder="e.g., Liver Qi Stagnation with Blood Deficiency"
                                className="w-full"
                              />
                            </div>
                            
                            {/* Signs & Symptoms */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Signs & Symptoms <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                value={caseStudy.signs_symptoms}
                                onChange={(e) => handleCaseStudyChange(index, 'signs_symptoms', e.target.value)}
                                placeholder="e.g., Irritability, mood swings, chest oppression, hypochondriac pain, sighing, irregular menstruation, fatigue, dizziness"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[80px]"
                              />
                            </div>
                            
                            {/* Tongue */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tongue Details <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={caseStudy.tongue}
                                onChange={(e) => handleCaseStudyChange(index, 'tongue', e.target.value)}
                                placeholder="e.g., Pale with thin white coating"
                                className="w-full"
                              />
                            </div>
                            
                            {/* Pulse */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Pulse Details <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={caseStudy.pulse}
                                onChange={(e) => handleCaseStudyChange(index, 'pulse', e.target.value)}
                                placeholder="e.g., Wiry and thready"
                                className="w-full"
                              />
                            </div>
                            
                            {/* Acupuncture Points */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Acupuncture Points Used <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                value={caseStudy.points}
                                onChange={(e) => handleCaseStudyChange(index, 'points', e.target.value)}
                                placeholder="e.g., LR-3, LR-14, GB-34, SP-6, ST-36, PC-6"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[60px]"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Progress Indicator */}
              {isGenerating && (
                <div className="mb-6 p-6 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-purple-900">Generating Mock Exam...</span>
                      <span className="text-sm font-bold text-purple-900">{generationProgress}%</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${generationProgress}%` }}
                      ></div>
                    </div>
                  </div>
                  {generationStep && (
                    <p className="text-sm text-purple-700">
                      {generationStep}
                    </p>
                  )}
                </div>
              )}

              {/* Generate Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating... (7-8 minutes)
                    </>
                  ) : (
                    'Generate Mock Exam'
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Generated Exam Preview */}
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h2 className="text-xl font-bold text-green-900">Mock Exam Generated Successfully!</h2>
                      <p className="text-green-700">Total Questions: {generatedExam.total_questions}</p>
                    </div>
                  </div>
                </div>

                {/* Time Limit Setting */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Time Limit (seconds)
                  </label>
                  <Input
                    type="number"
                    value={timeLimit}
                    onChange={(e) => setTimeLimit(parseInt(e.target.value))}
                    min="60"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Default: 8100 seconds (135 minutes = 1.5 minutes per question)
                  </p>
                </div>

                {/* Questions Preview */}
                <div className="mb-6 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold mb-4 text-gray-900">Questions Preview:</h3>
                  {generatedExam.questions.slice(0, 5).map((q, idx) => (
                    <div key={idx} className="mb-4 pb-4 border-b border-gray-200 last:border-b-0">
                      <p className="font-semibold text-gray-900">Q{idx + 1}: {q.question}</p>
                      <div className="ml-4 mt-2 space-y-1">
                        {q.options.map((opt, optIdx) => (
                          <p key={optIdx} className={`text-sm ${optIdx === q.correct_answer ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
                            {String.fromCharCode(65 + optIdx)}) {opt}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-gray-500 italic">...and {generatedExam.total_questions - 5} more questions</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    setGeneratedExam(null);
                    setCaseStudies(Array(15).fill(null).map(() => ({
                      diagnosis: '',
                      signs_symptoms: '',
                      tongue: '',
                      pulse: '',
                      points: ''
                    })));
                    setExpandedIndex(0);
                  }}
                >
                  Start Over
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-8 py-3"
                >
                  Save & Feature Mock Exam
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateMockExam;
